import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { AcademicState, SetStudentDetails, StartAcademicVerification, StudentDetails } from '@platform-frontend/checkout';
import { InclusiveActionFilter } from '@platform-frontend/ui-tools';
import { Navigate } from '@platform-frontend/util';
import { Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

interface StudentDetailsForm {
  studentNumber: FormControl<string>;
  studentId: FormControl<File | null>;
}

@UntilDestroy()
@Component({
  selector: 'yat-student-details',
  templateUrl: './student-details.component.html',
  styleUrls: ['./student-details.component.scss']
})
export class StudentDetailsComponent implements OnInit {
  constructor(private activatedRoute: ActivatedRoute, private store: Store) {}

  readonly studentDetailsForm = new FormGroup<StudentDetailsForm>({
    studentNumber: new FormControl('', { nonNullable: true, validators: Validators.required }),
    studentId: new FormControl(null, { nonNullable: true, validators: Validators.required })
  });

  @Select(AcademicState.studentDetails)
  studentDetails$: Observable<StudentDetails | null>;

  startAcademicVerificationActionFilter = new InclusiveActionFilter(StartAcademicVerification);

  ngOnInit(): void {
    this.studentDetails$
      .pipe(
        tap(studentDetails => this.initStudentDetails(studentDetails)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private initStudentDetails(studentDetails: StudentDetails | null): void {
    if (studentDetails) {
      this.studentDetailsForm.patchValue(studentDetails);
    } else {
      this.studentDetailsForm.reset();
    }
  }

  submitForm(value: StudentDetails): void {
    this.store
      .dispatch(new SetStudentDetails(value))
      .pipe(
        switchMap(() => this.store.dispatch(new StartAcademicVerification())),
        switchMap(() => this.store.dispatch(new Navigate(['../verify-academic-email'], { relativeTo: this.activatedRoute }))),
        untilDestroyed(this)
      )
      .subscribe();
  }

  goBack(): void {
    this.store
      .dispatch(
        new SetStudentDetails({ studentNumber: this.studentDetailsForm.value.studentNumber ?? '', studentId: this.studentDetailsForm.value.studentId ?? null })
      )
      .pipe(
        switchMap(() => this.store.dispatch(new Navigate(['../academic-registration'], { relativeTo: this.activatedRoute }))),
        untilDestroyed(this)
      )
      .subscribe();
  }
}
